import type { Car } from "../physics/car";
import type { Track } from "../track/track";
import {
  mat4Create,
  mat4Perspective,
  mat4LookAt,
  mat4Multiply,
  mat4FromTranslation,
  mat4FromRotationY,
  mat4FromRotationZ,
  mat4FromScaling,
  type Mat4,
} from "./math";
import vertSrc from "./shaders/main.vert";
import fragSrc from "./shaders/main.frag";

// --- Types ---

interface StaticMesh {
  vao: WebGLVertexArrayObject;
  count: number;
  mode: number;
}

export type CameraMode = "cockpit" | "chase" | "chase-north";

interface CameraState {
  x: number;
  y: number;
  z: number;
  tx: number;
  ty: number;
  tz: number;
  mode: CameraMode;
}

export interface Renderer {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  program: WebGLProgram;
  u: {
    model: WebGLUniformLocation;
    view: WebGLUniformLocation;
    projection: WebGLUniformLocation;
    color: WebGLUniformLocation;
    lightDir: WebGLUniformLocation;
    ambient: WebGLUniformLocation;
  };
  ground: StaticMesh;
  gravel: StaticMesh;
  surface: StaticMesh;
  curbL: StaticMesh;
  curbR: StaticMesh;
  edgeL: StaticMesh;
  edgeR: StaticMesh;
  startFinishWhite: StaticMesh;
  startFinishBlack: StaticMesh;
  centerDashes: StaticMesh;
  box: StaticMesh;
  cam: CameraState;
}

// --- Init ---

export function initWebGL(
  canvas: HTMLCanvasElement,
  track: Track
): Renderer {
  const gl = canvas.getContext("webgl2");
  if (!gl) throw new Error("WebGL 2 not supported");

  gl.enable(gl.DEPTH_TEST);
  gl.depthFunc(gl.LEQUAL);

  const program = createProgram(gl, vertSrc, fragSrc);
  gl.useProgram(program);

  const u = {
    model: gl.getUniformLocation(program, "uModel")!,
    view: gl.getUniformLocation(program, "uView")!,
    projection: gl.getUniformLocation(program, "uProjection")!,
    color: gl.getUniformLocation(program, "uColor")!,
    lightDir: gl.getUniformLocation(program, "uLightDir")!,
    ambient: gl.getUniformLocation(program, "uAmbient")!,
  };

  // Directional light from above-right
  const lx = 0.3,
    ly = 0.9,
    lz = 0.2;
  const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
  gl.uniform3f(u.lightDir, lx / lLen, ly / lLen, lz / lLen);
  gl.uniform3f(u.ambient, 0.3, 0.3, 0.35);

  // Build track geometry
  const cl = track.centerline;
  const hw = track.halfWidth;
  const laterals = computeLateralDirs(cl);

  const ground = createMesh(gl, program, buildGround(), gl.TRIANGLES);
  const gravel = createMesh(
    gl,
    program,
    buildStrip3D(cl, laterals, hw + 4, -0.01),
    gl.TRIANGLE_STRIP
  );
  const surface = createMesh(
    gl,
    program,
    buildStrip3D(cl, laterals, hw, 0.0),
    gl.TRIANGLE_STRIP
  );

  const curbOuter = hw + 1.2;
  const curbL = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw, curbOuter, 1, 0.005),
    gl.TRIANGLE_STRIP
  );
  const curbR = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw, curbOuter, -1, 0.005),
    gl.TRIANGLE_STRIP
  );

  const lineHW = 0.12;
  const edgeL = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw - lineHW, hw + lineHW, 1, 0.01),
    gl.TRIANGLE_STRIP
  );
  const edgeR = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw - lineHW, hw + lineHW, -1, 0.01),
    gl.TRIANGLE_STRIP
  );

  // Checkered start/finish line
  const checker = buildCheckerboard(cl, laterals, hw, track.startFinishIndex);
  const startFinishWhite = createMesh(gl, program, checker.white, gl.TRIANGLES);
  const startFinishBlack = createMesh(gl, program, checker.black, gl.TRIANGLES);

  // Center line dashes
  const centerDashes = createMesh(
    gl,
    program,
    buildCenterDashes(cl, laterals, track.startFinishIndex),
    gl.TRIANGLES
  );

  const box = createMesh(gl, program, buildBox(), gl.TRIANGLES);

  // Initialize camera at driver's eye position
  const startX = track.startX;
  const startZ = -track.startY;
  const cam: CameraState = {
    x: startX + 0.15,
    y: 1.1,
    z: startZ,
    tx: startX + 50,
    ty: 0.94,
    tz: startZ,
    mode: "cockpit",
  };

  return {
    gl,
    canvas,
    program,
    u,
    ground,
    gravel,
    surface,
    curbL,
    curbR,
    edgeL,
    edgeR,
    startFinishWhite,
    startFinishBlack,
    centerDashes,
    box,
    cam,
  };
}

// --- Render ---

export function renderScene(
  r: Renderer,
  car: Car,
  _track: Track,
  _alpha: number,
  gyroRoll: number = 0,
  steerInput: number = 0
): void {
  const { gl, canvas } = r;

  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.clearColor(0.55, 0.75, 0.95, 1.0); // sky blue
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

  gl.useProgram(r.program);

  // Speed-dependent FOV: 60° at rest → 75° at 300 km/h
  const speedMs = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
  const speedKmh = speedMs * 3.6;
  const speedFrac = Math.min(speedKmh / 300, 1);
  const baseFov = Math.PI / 3; // 60°
  const maxFov = (75 * Math.PI) / 180; // 75°
  const fov = baseFov + (maxFov - baseFov) * speedFrac;

  const aspect = canvas.width / canvas.height;
  const proj = mat4Perspective(fov, aspect, 0.1, 1000);
  gl.uniformMatrix4fv(r.u.projection, false, proj);

  // Camera
  updateCamera(r.cam, car, speedFrac);
  let view = mat4LookAt(
    [r.cam.x, r.cam.y, r.cam.z],
    [r.cam.tx, r.cam.ty, r.cam.tz],
    [0, 1, 0]
  );
  // Apply camera roll from device tilt (pre-multiply rolls in view space)
  if (gyroRoll !== 0) {
    view = mat4Multiply(mat4FromRotationZ(-gyroRoll), view);
  }
  gl.uniformMatrix4fv(r.u.view, false, view);

  const identity = mat4Create();

  // Ground
  drawMesh(r, r.ground, identity, [0.18, 0.42, 0.14, 1.0]);

  // Gravel
  drawMesh(r, r.gravel, identity, [0.65, 0.55, 0.35, 1.0]);

  // Track surface
  drawMesh(r, r.surface, identity, [0.2, 0.2, 0.22, 1.0]);

  // Curbs (red)
  drawMesh(r, r.curbL, identity, [0.85, 0.15, 0.1, 1.0]);
  drawMesh(r, r.curbR, identity, [0.85, 0.15, 0.1, 1.0]);

  // Edge lines (white)
  drawMesh(r, r.edgeL, identity, [1.0, 1.0, 1.0, 1.0]);
  drawMesh(r, r.edgeR, identity, [1.0, 1.0, 1.0, 1.0]);

  // Checkered start/finish line
  drawMesh(r, r.startFinishWhite, identity, [1.0, 1.0, 1.0, 1.0]);
  drawMesh(r, r.startFinishBlack, identity, [0.05, 0.05, 0.05, 1.0]);

  // Center line dashes
  drawMesh(r, r.centerDashes, identity, [1.0, 1.0, 1.0, 1.0]);

  // Car
  drawCar(r, car, steerInput);
}

function drawMesh(
  r: Renderer,
  mesh: StaticMesh,
  model: Mat4,
  color: readonly number[]
): void {
  const { gl } = r;
  gl.uniformMatrix4fv(r.u.model, false, model);
  gl.uniform4fv(r.u.color, color);
  gl.bindVertexArray(mesh.vao);
  gl.drawArrays(mesh.mode, 0, mesh.count);
}

// --- Camera ---

function updateCamera(cam: CameraState, car: Car, speedFrac: number): void {
  const carX = car.x;
  const carZ = -car.y;
  const cosA = Math.cos(car.angle);
  const sinA = Math.sin(car.angle);

  let desiredX: number, desiredY: number, desiredZ: number;
  let targetX: number, targetY: number, targetZ: number;

  if (cam.mode === "cockpit") {
    // First-person: driver's eye position
    const eyeForward = 0.15;
    const eyeHeight = 1.1;
    desiredX = carX + cosA * eyeForward;
    desiredY = eyeHeight;
    desiredZ = carZ - sinA * eyeForward;

    // Speed-dependent look-ahead: 50m at rest → 80m at top speed
    const lookAhead = 50 + 30 * speedFrac;
    targetX = carX + cosA * lookAhead;
    targetY = eyeHeight * 0.85;
    targetZ = carZ - sinA * lookAhead;
  } else if (cam.mode === "chase") {
    // Chase camera: behind and above the car, follows heading
    const chaseDist = 10 + 4 * speedFrac; // 10-14m behind
    const chaseHeight = 4 + 2 * speedFrac; // 4-6m up
    desiredX = carX - cosA * chaseDist;
    desiredY = chaseHeight;
    desiredZ = carZ + sinA * chaseDist;

    // Look slightly ahead of the car
    const lookAhead = 8 + 12 * speedFrac;
    targetX = carX + cosA * lookAhead;
    targetY = 0.5;
    targetZ = carZ - sinA * lookAhead;
  } else {
    // Chase-north: fixed direction camera (always from south, looking north)
    const chaseDist = 10 + 4 * speedFrac;
    const chaseHeight = 4 + 2 * speedFrac;
    desiredX = carX;
    desiredY = chaseHeight;
    desiredZ = carZ + chaseDist; // always south of car (+Z direction)

    targetX = carX;
    targetY = 0.5;
    targetZ = carZ - 5; // look slightly ahead (north)
  }

  const lerp = 0.12;
  cam.x += (desiredX - cam.x) * lerp;
  cam.y += (desiredY - cam.y) * lerp;
  cam.z += (desiredZ - cam.z) * lerp;
  cam.tx += (targetX - cam.tx) * lerp;
  cam.ty += (targetY - cam.ty) * lerp;
  cam.tz += (targetZ - cam.tz) * lerp;
}

export function setCameraMode(r: Renderer, mode: CameraMode): void {
  r.cam.mode = mode;
}

export function getCameraMode(r: Renderer): CameraMode {
  return r.cam.mode;
}

// --- Car ---

interface CarPart {
  cx: number;
  cy: number;
  cz: number;
  sx: number;
  sy: number;
  sz: number;
  color: readonly number[];
}

const CAR_RED = [0.85, 0.1, 0.1, 1.0];
const CAR_GRAY = [0.2, 0.2, 0.2, 1.0];
const CAR_BLACK = [0.08, 0.08, 0.08, 1.0];
const CAR_DARK = [0.03, 0.03, 0.03, 1.0];

const CAR_PARTS: CarPart[] = [
  // Main body
  { cx: -0.25, cy: 0.35, cz: 0, sx: 4.5, sy: 0.4, sz: 1.8, color: CAR_RED },
  // Nose
  { cx: 2.425, cy: 0.275, cz: 0, sx: 0.85, sy: 0.25, sz: 0.8, color: CAR_RED },
  // Cockpit opening (dark)
  { cx: 0.15, cy: 0.58, cz: 0, sx: 0.9, sy: 0.08, sz: 0.7, color: CAR_DARK },
  // Front wing
  { cx: 2.675, cy: 0.1, cz: 0, sx: 0.35, sy: 0.06, sz: 2.0, color: CAR_GRAY },
  // Rear wing main plane
  { cx: -2.675, cy: 0.88, cz: 0, sx: 0.35, sy: 0.08, sz: 2.0, color: CAR_GRAY },
  // Rear wing endplates
  {
    cx: -2.675,
    cy: 0.7,
    cz: -1.0,
    sx: 0.35,
    sy: 0.45,
    sz: 0.05,
    color: CAR_GRAY,
  },
  {
    cx: -2.675,
    cy: 0.7,
    cz: 1.0,
    sx: 0.35,
    sy: 0.45,
    sz: 0.05,
    color: CAR_GRAY,
  },
  // Rear wing support pillars
  {
    cx: -2.4,
    cy: 0.62,
    cz: -0.3,
    sx: 0.05,
    sy: 0.32,
    sz: 0.05,
    color: CAR_GRAY,
  },
  {
    cx: -2.4,
    cy: 0.62,
    cz: 0.3,
    sx: 0.05,
    sy: 0.32,
    sz: 0.05,
    color: CAR_GRAY,
  },
  // Rear wheels
  {
    cx: -1.6,
    cy: 0.33,
    cz: -1.05,
    sx: 0.8,
    sy: 0.65,
    sz: 0.35,
    color: CAR_BLACK,
  },
  {
    cx: -1.6,
    cy: 0.33,
    cz: 1.05,
    sx: 0.8,
    sy: 0.65,
    sz: 0.35,
    color: CAR_BLACK,
  },
  // Front wheels
  {
    cx: 1.4,
    cy: 0.28,
    cz: -0.75,
    sx: 0.6,
    sy: 0.55,
    sz: 0.28,
    color: CAR_BLACK,
  },
  {
    cx: 1.4,
    cy: 0.28,
    cz: 0.75,
    sx: 0.6,
    sy: 0.55,
    sz: 0.28,
    color: CAR_BLACK,
  },
];

// Indices of front wheel parts in CAR_PARTS (left=10, right=11)
const FRONT_WHEEL_INDICES = [10, 11];

function drawCar(r: Renderer, car: Car, steerInput: number = 0): void {
  // Car model matrix: translate to 3D position, rotate to heading
  // Physics (x, y) → 3D (x, 0, -y), rotateY(angle)
  const carModel = mat4Multiply(
    mat4FromTranslation(car.x, 0, -car.y),
    mat4FromRotationY(car.angle)
  );

  // Visual steering angle for front wheels (negate to match physics sign convention)
  const wheelSteerAngle = -steerInput * car.maxSteerAngle;

  for (let i = 0; i < CAR_PARTS.length; i++) {
    const part = CAR_PARTS[i];
    let partModel: Mat4;

    if (FRONT_WHEEL_INDICES.includes(i)) {
      // Front wheels: translate to position, rotate by steering, then scale
      partModel = mat4Multiply(
        mat4Multiply(
          mat4Multiply(carModel, mat4FromTranslation(part.cx, part.cy, part.cz)),
          mat4FromRotationY(wheelSteerAngle)
        ),
        mat4FromScaling(part.sx, part.sy, part.sz)
      );
    } else {
      partModel = mat4Multiply(
        mat4Multiply(carModel, mat4FromTranslation(part.cx, part.cy, part.cz)),
        mat4FromScaling(part.sx, part.sy, part.sz)
      );
    }
    drawMesh(r, r.box, partModel, part.color);
  }
}

// --- Geometry builders ---

// Compute lateral directions (perpendicular to centerline) in 2D
function computeLateralDirs(
  cl: { x: number; y: number }[]
): { nx: number; ny: number }[] {
  const normals: { nx: number; ny: number }[] = [];
  for (let i = 0; i < cl.length; i++) {
    const prev = cl[(i - 1 + cl.length) % cl.length];
    const next = cl[(i + 1) % cl.length];
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const len = Math.sqrt(tx * tx + ty * ty) || 1;
    normals.push({ nx: -ty / len, ny: tx / len });
  }
  return normals;
}

// Build a symmetric track strip (centered on centerline)
function buildStrip3D(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  halfWidth: number,
  height: number
): Float32Array {
  const data: number[] = [];
  for (let i = 0; i < cl.length; i++) {
    const cx = cl[i].x;
    const cz = -cl[i].y;
    const nx = laterals[i].nx;
    const nz = -laterals[i].ny;
    // Left vertex
    data.push(cx + nx * halfWidth, height, cz + nz * halfWidth, 0, 1, 0);
    // Right vertex
    data.push(cx - nx * halfWidth, height, cz - nz * halfWidth, 0, 1, 0);
  }
  // Close the loop
  data.push(data[0], data[1], data[2], data[3], data[4], data[5]);
  data.push(data[6], data[7], data[8], data[9], data[10], data[11]);
  return new Float32Array(data);
}

// Build a one-sided strip (e.g. curbs, edge lines)
function buildSideStrip3D(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  innerHW: number,
  outerHW: number,
  side: 1 | -1,
  height: number
): Float32Array {
  const data: number[] = [];
  for (let i = 0; i < cl.length; i++) {
    const cx = cl[i].x;
    const cz = -cl[i].y;
    const nx = laterals[i].nx;
    const nz = -laterals[i].ny;
    data.push(
      cx + nx * innerHW * side,
      height,
      cz + nz * innerHW * side,
      0,
      1,
      0
    );
    data.push(
      cx + nx * outerHW * side,
      height,
      cz + nz * outerHW * side,
      0,
      1,
      0
    );
  }
  // Close
  data.push(data[0], data[1], data[2], data[3], data[4], data[5]);
  data.push(data[6], data[7], data[8], data[9], data[10], data[11]);
  return new Float32Array(data);
}

// Build a checkered start/finish line: returns separate white and black square meshes
function buildCheckerboard(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  halfWidth: number,
  index: number
): { white: Float32Array; black: Float32Array } {
  const cx = cl[index].x;
  const cz = -cl[index].y;
  const lx = laterals[index].nx;
  const lz = -laterals[index].ny;

  // Tangent direction along the track
  const prev = cl[(index - 1 + cl.length) % cl.length];
  const next = cl[(index + 1) % cl.length];
  const tx = next.x - prev.x;
  const ty = next.y - prev.y;
  const tLen = Math.sqrt(tx * tx + ty * ty) || 1;
  const tdx = tx / tLen;
  const tdz = -ty / tLen;

  const lineHalfWidth = 1.5; // 3m wide checkerboard
  const h = 0.02;
  const squareSize = 1.0; // 1m squares
  const cols = Math.round((2 * halfWidth) / squareSize);
  const rows = Math.round((2 * lineHalfWidth) / squareSize);

  const whiteVerts: number[] = [];
  const blackVerts: number[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const verts = (r + c) % 2 === 0 ? whiteVerts : blackVerts;

      // Lateral: from +halfWidth to -halfWidth
      const lat0 = halfWidth - c * squareSize;
      const lat1 = halfWidth - (c + 1) * squareSize;
      // Tangent: from -lineHalfWidth to +lineHalfWidth
      const tan0 = -lineHalfWidth + r * squareSize;
      const tan1 = -lineHalfWidth + (r + 1) * squareSize;

      // Four corners
      const ax = cx + lx * lat0 + tdx * tan0;
      const az = cz + lz * lat0 + tdz * tan0;
      const bx = cx + lx * lat0 + tdx * tan1;
      const bz = cz + lz * lat0 + tdz * tan1;
      const dx = cx + lx * lat1 + tdx * tan1;
      const dz = cz + lz * lat1 + tdz * tan1;
      const ex = cx + lx * lat1 + tdx * tan0;
      const ez = cz + lz * lat1 + tdz * tan0;

      // Two triangles (6 vertices, each with pos + normal)
      verts.push(
        ax, h, az, 0, 1, 0, bx, h, bz, 0, 1, 0, dx, h, dz, 0, 1, 0,
        ax, h, az, 0, 1, 0, dx, h, dz, 0, 1, 0, ex, h, ez, 0, 1, 0
      );
    }
  }

  return {
    white: new Float32Array(whiteVerts),
    black: new Float32Array(blackVerts),
  };
}

// Build dashed center line along the track
function buildCenterDashes(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  startFinishIndex: number
): Float32Array {
  const data: number[] = [];
  const dashHW = 0.1; // 0.2m wide
  const h = 0.01;

  for (let i = 0; i < cl.length; i++) {
    // Dash every other segment
    if (i % 2 !== 0) continue;

    // Skip near start/finish line
    const dist = Math.min(
      Math.abs(i - startFinishIndex),
      cl.length - Math.abs(i - startFinishIndex)
    );
    if (dist < 3) continue;

    const j = (i + 1) % cl.length;
    const cx1 = cl[i].x, cz1 = -cl[i].y;
    const nx1 = laterals[i].nx, nz1 = -laterals[i].ny;
    const cx2 = cl[j].x, cz2 = -cl[j].y;
    const nx2 = laterals[j].nx, nz2 = -laterals[j].ny;

    // Quad from point i to point i+1, offset ±dashHW from center
    const x1 = cx1 + nx1 * dashHW, z1 = cz1 + nz1 * dashHW;
    const x2 = cx1 - nx1 * dashHW, z2 = cz1 - nz1 * dashHW;
    const x3 = cx2 - nx2 * dashHW, z3 = cz2 - nz2 * dashHW;
    const x4 = cx2 + nx2 * dashHW, z4 = cz2 + nz2 * dashHW;

    data.push(
      x1, h, z1, 0, 1, 0, x4, h, z4, 0, 1, 0, x3, h, z3, 0, 1, 0,
      x1, h, z1, 0, 1, 0, x3, h, z3, 0, 1, 0, x2, h, z2, 0, 1, 0
    );
  }

  return new Float32Array(data);
}

function buildGround(): Float32Array {
  const s = 500;
  const y = -0.05;
  // Two triangles forming a large quad
  // prettier-ignore
  return new Float32Array([
    -s, y, -s, 0, 1, 0,   s, y, -s, 0, 1, 0,   s, y,  s, 0, 1, 0,
    -s, y, -s, 0, 1, 0,   s, y,  s, 0, 1, 0,  -s, y,  s, 0, 1, 0,
  ]);
}

function buildBox(): Float32Array {
  // Unit box centered at origin, each vertex: pos(3) + normal(3)
  // prettier-ignore
  return new Float32Array([
    // +Y face (top)
    -0.5,  0.5, -0.5,  0,  1,  0,   0.5,  0.5, -0.5,  0,  1,  0,   0.5,  0.5,  0.5,  0,  1,  0,
    -0.5,  0.5, -0.5,  0,  1,  0,   0.5,  0.5,  0.5,  0,  1,  0,  -0.5,  0.5,  0.5,  0,  1,  0,
    // -Y face (bottom)
    -0.5, -0.5,  0.5,  0, -1,  0,   0.5, -0.5,  0.5,  0, -1,  0,   0.5, -0.5, -0.5,  0, -1,  0,
    -0.5, -0.5,  0.5,  0, -1,  0,   0.5, -0.5, -0.5,  0, -1,  0,  -0.5, -0.5, -0.5,  0, -1,  0,
    // +X face (front)
     0.5, -0.5, -0.5,  1,  0,  0,   0.5,  0.5, -0.5,  1,  0,  0,   0.5,  0.5,  0.5,  1,  0,  0,
     0.5, -0.5, -0.5,  1,  0,  0,   0.5,  0.5,  0.5,  1,  0,  0,   0.5, -0.5,  0.5,  1,  0,  0,
    // -X face (back)
    -0.5, -0.5,  0.5, -1,  0,  0,  -0.5,  0.5,  0.5, -1,  0,  0,  -0.5,  0.5, -0.5, -1,  0,  0,
    -0.5, -0.5,  0.5, -1,  0,  0,  -0.5,  0.5, -0.5, -1,  0,  0,  -0.5, -0.5, -0.5, -1,  0,  0,
    // +Z face (right)
     0.5, -0.5,  0.5,  0,  0,  1,   0.5,  0.5,  0.5,  0,  0,  1,  -0.5,  0.5,  0.5,  0,  0,  1,
     0.5, -0.5,  0.5,  0,  0,  1,  -0.5,  0.5,  0.5,  0,  0,  1,  -0.5, -0.5,  0.5,  0,  0,  1,
    // -Z face (left)
    -0.5, -0.5, -0.5,  0,  0, -1,  -0.5,  0.5, -0.5,  0,  0, -1,   0.5,  0.5, -0.5,  0,  0, -1,
    -0.5, -0.5, -0.5,  0,  0, -1,   0.5,  0.5, -0.5,  0,  0, -1,   0.5, -0.5, -0.5,  0,  0, -1,
  ]);
}

// --- Mesh creation ---

function createMesh(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  data: Float32Array,
  mode: number
): StaticMesh {
  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);

  const vbo = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);

  const aPos = gl.getAttribLocation(program, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 24, 0);

  const aNormal = gl.getAttribLocation(program, "aNormal");
  gl.enableVertexAttribArray(aNormal);
  gl.vertexAttribPointer(aNormal, 3, gl.FLOAT, false, 24, 12);

  gl.bindVertexArray(null);

  return { vao, count: data.length / 6, mode };
}

// --- Shader helpers ---

function createShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string
): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
}

function createProgram(
  gl: WebGL2RenderingContext,
  vs: string,
  fs: string
): WebGLProgram {
  const vert = createShader(gl, gl.VERTEX_SHADER, vs);
  const frag = createShader(gl, gl.FRAGMENT_SHADER, fs);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vert);
  gl.attachShader(prog, frag);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(prog);
    throw new Error(`Program link error: ${info}`);
  }
  return prog;
}

// --- Shader HMR ---

let activeRenderer: Renderer | null = null;

export function setActiveRenderer(r: Renderer): void {
  activeRenderer = r;
}

function hotReloadShaders(newVert: string, newFrag: string): void {
  if (!activeRenderer) return;
  const { gl } = activeRenderer;
  try {
    // Bind attribute locations to match existing VAOs
    const vert = createShader(gl, gl.VERTEX_SHADER, newVert);
    const frag = createShader(gl, gl.FRAGMENT_SHADER, newFrag);
    const program = gl.createProgram()!;
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    const oldProg = activeRenderer.program;
    gl.bindAttribLocation(program, gl.getAttribLocation(oldProg, "aPos"), "aPos");
    gl.bindAttribLocation(program, gl.getAttribLocation(oldProg, "aNormal"), "aNormal");
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      throw new Error(`Program link error: ${info}`);
    }
    gl.deleteProgram(oldProg);
    activeRenderer.program = program;
    gl.useProgram(program);
    activeRenderer.u = {
      model: gl.getUniformLocation(program, "uModel")!,
      view: gl.getUniformLocation(program, "uView")!,
      projection: gl.getUniformLocation(program, "uProjection")!,
      color: gl.getUniformLocation(program, "uColor")!,
      lightDir: gl.getUniformLocation(program, "uLightDir")!,
      ambient: gl.getUniformLocation(program, "uAmbient")!,
    };
    // Re-set lighting uniforms
    const lx = 0.3, ly = 0.9, lz = 0.2;
    const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
    gl.uniform3f(activeRenderer.u.lightDir, lx / lLen, ly / lLen, lz / lLen);
    gl.uniform3f(activeRenderer.u.ambient, 0.3, 0.3, 0.35);
    console.log("[HMR] Shaders recompiled");
  } catch (e) {
    console.error("[HMR] Shader recompilation failed:", e);
  }
}

if (import.meta.hot) {
  import.meta.hot.accept("./shaders/main.vert", (mod) => {
    if (mod) hotReloadShaders(mod.default, fragSrc);
  });
  import.meta.hot.accept("./shaders/main.frag", (mod) => {
    if (mod) hotReloadShaders(vertSrc, mod.default);
  });
}
